import React, { useEffect, useRef } from 'react';
import { Box } from '@material-ui/core';
import NavBar from '../components/NavBar';
import { useState } from 'react';
import Footer from '../components/Footer.js';
import Paper from '@material-ui/core/Paper';
import { CircularProgress } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { Prompt, Redirect, useHistory } from 'react-router-dom';
import { useLocation } from "react-router-dom";

import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
} from '@material-ui/core';

import surveyComplete from '../resources/surveyComplete.png';
import { isMobile } from '../utils/util';
import SurveyReact from '../components/SurveyReact';
import { createSurveyAutosave } from '../utils/surveyAutosave';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function Questions() {
    const [completed, setCompleted] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validCohort, setValidCohort] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true/false = result
    const [surveyReady, setSurveyReady] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [saveStatus, setSaveStatus] = useState('idle');
    const [saveError, setSaveError] = useState('');
    const [submissionError, setSubmissionError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [exiting, setExiting] = useState(false);
    const [resumed, setResumed] = useState(false);
    const saver = useRef(null);
    const busy = useRef(false);
    const finalSnapshot = useRef(null);

    const history = useHistory();
    const location = useLocation();

    const [defaultValue, setDefaultValue] = useState({
        ID: undefined,
        currentPageNo: 0,
        data: {},
    });

    const query = useQuery();
    const cohortId = query.get("cohortId") || null;

    const [selectedSurvey, setSelectedSurvey] = useState(false);

    // Check if user is logged in
    useEffect(() => {
        fetch('/api/auth/verify', {
            method: 'GET',
            credentials: 'include',
        })
            .then((response) => {
                if (response.ok) {
                    setIsAuthenticated(true);
                } else {
                    setIsAuthenticated(false);
                }
            })
            .catch(() => {
                setIsAuthenticated(false);
            });
    }, []);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (isAuthenticated === false) {
            // Preserve the current URL including cohortId
            const currentUrl = location.pathname + location.search;
            history.push(`/login?redirect=${encodeURIComponent(currentUrl)}`);
        }
    }, [isAuthenticated, location, history]);

    const initSurveyData = () => {
        setLoading(true);
        setLoadError('');
        fetch(`/api/results/lastest`, {
            method: 'GET',
            credentials: 'same-origin',
        })
            .then((response) => {
                if (!response.ok) throw new Error('Unable to load your saved assessment. Please sign in again if your session has expired.');
                return response.json();
            })
            .then(
                (data) => {
                    if (data && data.ID) {
                        const { currentPageNo, ID, ...rest } = data;
                        setDefaultValue({
                            ID,
                            currentPageNo,
                            data: rest,
                        });
                        setResumed(Number(currentPageNo) > 0 || data.hasSavedAnswers === true);
                        setSurveyReady(true);
                    } else {
                        throw new Error('Your assessment could not be loaded. Please retry before entering answers.');
                    }
                },
            )
            .catch((err) => setLoadError(err.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (isAuthenticated === true) {
            initSurveyData();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!surveyReady) return undefined;
        const controller = createSurveyAutosave({
            onStatus: setSaveStatus,
            save: async (snapshot) => {
                const response = await fetch('/api/results', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin',
                    body: JSON.stringify({ ID: defaultValue.ID, ...(cohortId && { cohortId }), ...snapshot }),
                });
                if (!response.ok) {
                    const message = response.status === 401
                        ? 'Your session has expired. Keep this page open and sign in again in another tab using the same account, then retry saving.'
                        : 'Your latest changes have not been saved. Keep this page open, check your connection and retry saving.';
                    setSaveError(message);
                    throw new Error(message);
                }
                setSaveError('');
            },
        });
        saver.current = controller;
        const beforeUnload = (event) => {
            if (controller.isDirty() || busy.current) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        const whenHidden = () => {
            if (document.visibilityState === 'hidden') controller.flush().catch(() => {});
        };
        window.addEventListener('beforeunload', beforeUnload);
        document.addEventListener('visibilitychange', whenHidden);
        return () => {
            controller.dispose();
            window.removeEventListener('beforeunload', beforeUnload);
            document.removeEventListener('visibilitychange', whenHidden);
        };
        // The saved assessment ID and cohort are fixed for this mounted assessment.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyReady, defaultValue.ID, cohortId]);

    useEffect(() => {
        if (cohortId) {
            fetch(`/api/cohort/${cohortId}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            })
                .then(res => {
                    if (res.ok) return res.json();
                    throw new Error("Invalid cohort");
                })
                .then(data => {
                    if (data && data.id) {
                        setValidCohort(true);
                    } else {
                        setValidCohort(false);
                    }
                })
                .catch(() => setValidCohort(false));
        } else {
            setValidCohort(true);
        }
    }, [cohortId]);

    const onPageChanged = (res) => {
        if (!busy.current && saver.current) saver.current.update({ results: res.data, currentPageNo: res.currentPageNo });
    };

    const onCompleteComponent = async (res) => {
        if (busy.current || !saver.current) return;
        const snapshot = { results: JSON.parse(JSON.stringify(res.data)), currentPageNo: res.currentPageNo };
        const changed = JSON.stringify(snapshot) !== JSON.stringify(finalSnapshot.current);
        finalSnapshot.current = snapshot;
        busy.current = true;
        setSubmitting(true);
        setSubmissionError('');
        try {
            if (changed) saver.current.update(finalSnapshot.current);
            await saver.current.flush();
            const response = await fetch('/api/results', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                results: finalSnapshot.current.results,
                ID: defaultValue.ID,
                cohortId,
            }),
            });
            if (!response.ok) throw new Error('Submission failed');
            await response.json();
            saver.current.stop();
            setCompleted(true);
        } catch (error) {
            setSubmissionError('We could not confirm your submission. Keep this page open and retry. Your assessment is not marked complete on this screen until the server confirms it.');
        } finally {
            busy.current = false;
            setSubmitting(false);
        }
    };

    const saveAndExit = async () => {
        if (busy.current || !saver.current) return;
        busy.current = true;
        setExiting(true);
        try {
            await saver.current.flush();
            history.push('/profile');
        } catch (error) {
            // Stay on the assessment with the answers intact and show the save error.
        } finally {
            busy.current = false;
            setExiting(false);
        }
    };

    let getSurveyResults = (ID) => {
        setLoading(true);
        ID = defaultValue.ID;
        console.log(ID);
        let getSurvey = '/api/results?SurveyResults=' + ID;
        fetch(getSurvey, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        })
            .then((response) => response.json())
            .then((data) => {
                setSelectedSurvey(data);
            })
            .finally(() => setLoading(false));
    };

    const handleClose = () => {
        setOpen(false);
    };

    // Show loading while checking authentication
    if (isAuthenticated === null || validCohort === null) {
        return (
            <CircularProgress
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
            />
        );
    }

    // If not authenticated, return null (redirect will happen via useEffect)
    if (isAuthenticated === false) {
        return null;
    }

    if (validCohort === false) {
        return <Redirect to="/error?errCode=404" />;
    }

    if (!surveyReady) {
        return <div style={{ padding: 32 }}>
            <NavBar isLoggedIn={true} />
            {loadError ? <div role="alert"><p>{loadError}</p><Button onClick={initSurveyData}>Retry loading assessment</Button></div> : <CircularProgress aria-label="Loading saved assessment" />}
        </div>;
    }

    if (selectedSurvey === false) {

        if (completed === false) {
            return (
                <div
                    style={{
                        backgroundColor: '#f5f8f5',
                        minHeight: '100vh',
                    }}
                >
                    <NavBar isLoggedIn={true} />
                    <Prompt when={saveStatus === 'pending' || saveStatus === 'saving' || saveStatus === 'error' || submitting}
                        message={() => saver.current && saver.current.isDirty() ? 'Your latest changes may not be saved. Stay on this page and use Save and exit before leaving.' : submitting ? 'Please wait for your submission to finish.' : true} />
                    <div
                        style={{
                            margin: 'auto',
                            maxWidth: '1440px',
                            padding: isMobile ? '0' : '30px 0 50px',
                        }}
                    >
                        <Box
                            boxShadow={4}
                            color="text.primary"
                            className={isMobile ? 'm-survey' : ''}
                        >
                            <Paper className={`emap-save-bar emap-save-bar--${saveStatus}`}>
                                {resumed && <Typography>Your unfinished assessment has been restored. Continue from your saved page.</Typography>}
                                <Typography className="emap-save-status" role="status" aria-live="polite">
                                    {submitting ? 'Submitting assessment...' : exiting ? 'Saving before you leave...' : ({ idle: 'Answers save automatically. Use Save and exit before leaving.', pending: 'Changes waiting to save...', saving: 'Saving your progress...', saved: 'All changes saved.', error: saveError || 'Your latest changes have not been saved. Check your connection and retry.' })[saveStatus]}
                                </Typography>
                                {saveStatus === 'error' && <Button disabled={submitting || exiting} onClick={() => saver.current.flush().catch(() => {})}>Retry saving</Button>}
                                {submissionError && <div role="alert"><Typography>{submissionError}</Typography><Button disabled={submitting} onClick={() => onCompleteComponent({ data: finalSnapshot.current.results, currentPageNo: finalSnapshot.current.currentPageNo })}>Retry submission</Button></div>}
                                <Button className="emap-save-exit" variant="outlined" disabled={submitting || exiting || !!submissionError} onClick={saveAndExit}>Save and exit</Button>
                            </Paper>
                            <fieldset disabled={submitting || exiting || !!submissionError} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                            <SurveyReact
                                onComplete={onCompleteComponent}
                                onPageChanged={onPageChanged}
                                onValueChanged={onPageChanged}
                                defaultValue={defaultValue}
                            />
                            </fieldset>
                        </Box>
                    </div>

                    <Dialog onClose={handleClose} open={open}>
                        <DialogTitle>Tips</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                You havent't done all the question yet! Click the button to go
                                back to the questionnarie!
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                autoFocus
                                color="primary"
                                variant="contained"
                            >
                                Back
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            );
        } else {
            return (
                <div>
                    <div
                        style={{
                            backgroundColor: '#F8F8F8',
                            minHeight: '100vh',
                        }}
                    >
                        <NavBar isLoggedIn={true} />
                        <Paper
                            style={{
                                margin: 'auto',
                                maxWidth: '850px',
                                marginTop: '50px',
                                padding: '30px',
                            }}
                        >
                            <Typography variant="h5">Evaluation Completed</Typography>
                            <Divider />
                            <Grid container spacing={4}>
                                <Grid
                                    item
                                    xs="12"
                                    sm="7"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        paragraph
                                        style={{ wordWrap: 'break-word' }}
                                    >
                                        Thank you for completing the Entremap Entrepreneurial Mindset
                                        Activity Profile. We suggest returning to Entremap every 6 to
                                        12 months to gage progress over time.
                                    </Typography>
                                </Grid>
                                <Grid item xs="12" sm="3">
                                    <img
                                        src={surveyComplete}
                                        width="315px"
                                        style={{ paddingTop: '10px' }}
                                        alt="Survey Complete"
                                    ></img>
                                </Grid>
                            </Grid>

                            <Typography
                                variant="h6"
                                style={{
                                    paddingTop: '10px',
                                    paddingBottom: '10px',
                                    textAlign: 'center',
                                }}
                            >
                                Click below to view your results.
                            </Typography>

                            <div style={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        getSurveyResults(defaultValue);
                                    }}
                                >
                                    Continue
                                </Button>
                            </div>
                        </Paper>
                        <div
                            className="emap-assessment-footer"
                        >
                            <Footer />
                        </div>
                    </div>
                </div>
            );
        }
    } else {
        return (
            <Redirect
                push
                to={{ pathname: '/results', state: selectedSurvey }}
            ></Redirect>
        );
    }
}

export default Questions;

